import { useState, useEffect } from "react";
import Confetti from "react-confetti";
import Button from "../../components/ui/button/Button.tsx";
import { useParams } from "react-router";
import api from "../../services/api";
import { AsaasFinancialNotice } from "../../components/common/AsaasSeal";

// Interfaces para tipagem dos dados da API
interface BoletoData {
    boletoCode: string;
    [key: string]: any;
}

interface OrderItem {
    id: string;
    orderItemId: string;
    orderId: string;
    productId?: string;
    type?: string;
    unitPrice: number;
    totalPrice: number;
    quantity?: number;
    discount?: number;
    status?: string;
    createdAt?: string;
    updatedAt?: string;
    settingsData?: any;
    product?: Product;
}

interface Product {
    id: string;
    productId: string;
    name: string;
    description: string;
    imageUrl: string;
    price: number;
    discount: number;
    stock: number;
    status: string;
    createdAt: string;
    updatedAt: string;
    settingsData: any;
    supportInfo: SupportInfo;
}

interface SupportInfo {
    id: string;
    supportInfoId: string;
    email: string;
    phone: string;
    address: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
    createdAt: string;
    updatedAt: string;
}

interface Customer {
    id: string;
    customerId: string;
    name: string;
    email: string;
    phone?: string;
    document?: string;
    documentType?: string;
    [key: string]: any;
}

interface Company {
    id: string;
    companyId: string;
    name: string;
    fantasyName: string;
    email: string;
    phone?: string;
    document?: string;
    documentType?: string;
    [key: string]: any;
}

interface OrderData {
    id: string;
    code: string;
    status: string;
    createdAt: string;
    updatedAt: string;
    totalAmount: number;
    discountAmount?: number;
    method: string;
    gateway?: string;
}

interface BoletoResponse {
    status: string;
    data: {
        order: OrderData;
        items: OrderItem[];
        customer: Customer;
        company?: Company;
        payments?: any[];
        boleto: BoletoData;
    };
}

const FinalizationBoleto = () => {
    const { id } = useParams<{ id: string }>();
    const [showConfetti, setShowConfetti] = useState<boolean>(true);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [orderData, setOrderData] = useState<BoletoResponse | null>(null);
    const [copied, setCopied] = useState<boolean>(false);

    // Função para buscar os dados do pagamento Boleto seguindo o padrão do projeto
    const fetchBoletoData = async () => {
        try {
            setLoading(true);

            // Verificar se temos o id da URL
            if (!id) {
                setError("ID do pedido não encontrado.");
                setLoading(false);
                return;
            }

            // Preparar o URL
            const url = `/api/payments/pending-boleto/${id}`;

            // Chamada para a API
            const response = await api.get(url);

            // Verificar se a resposta tem o formato esperado
            if (response.data && response.data.status === 'success') {
                setOrderData(response.data);
            } else {
                console.error("Erro ao carregar dados do Boleto:", response.data);
                setError("Não foi possível carregar os dados do pagamento Boleto.");
            }
        } catch (err) {
            console.error("Erro ao buscar dados do Boleto:", err);
            setError("Não foi possível carregar os dados do Boleto. Por favor, tente novamente.");
        } finally {
            setLoading(false);
        }
    };

    // Efeito para chamar a função de busca quando o componente montar
    useEffect(() => {
        fetchBoletoData();
    }, [id]);

    // Efeito para confetti
    useEffect(() => {
        const timer = setTimeout(() => setShowConfetti(false), 5000);
        return () => clearTimeout(timer);
    }, []);

    // Função para copiar o código Boleto
    const copyBoletoCode = () => {
        const linhaDigitavel = orderData?.data.boleto?.linhaDigitavel;
        if (linhaDigitavel) {
            navigator.clipboard.writeText(linhaDigitavel)
                .then(() => {
                    setCopied(true);
                    setTimeout(() => setCopied(false), 3000);
                })
                .catch((err) => {
                    console.error("Erro ao copiar código do Boleto:", err);
                });
        }
    };

    // Se estiver carregando, mostrar spinner
    if (loading) {
        return (
            <div className="h-screen flex items-center justify-center">
                <div className="text-center">
                    <div className="spinner-border animate-spin inline-block w-8 h-8 border-4 rounded-full text-blue-600" role="status">
                        <span className="sr-only">Carregando...</span>
                    </div>
                    <p>Carregando dados do Boleto...</p>
                </div>
            </div>
        );
    }

    // Se houver erro, mostrar mensagem
    if (error || !orderData) {
        return (
            <div className="h-screen flex items-center justify-center">
                <div className="text-center max-w-md p-6 bg-white shadow-lg rounded-lg">
                    <div className="text-red-500 text-8xl mb-4">✗</div>
                    <h2 className="text-xl font-semibold mb-4">Erro ao carregar pagamento</h2>
                    <p className="mb-6">{error || "Dados do pagamento não encontrados."}</p>
                </div>
            </div>
        );
    }

    return (
        <>
            {showConfetti && (
                <Confetti
                    width={window.innerWidth}
                    height={window.innerHeight}
                    recycle={false}
                    numberOfPieces={200}
                />
            )}
            <div className="max-w-7xl mx-auto py-3 pr-3 pl-3">
                <div className="w-full md:w-3/5 mx-auto bg-white shadow-lg rounded-lg p-6 mt-6">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-8 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <span className="text-green-600 text-xl">✔</span>
                            <h1 className="text-lg font-semibold text-green-600">Boleto gerado com sucesso</h1>
                        </div>
                    </div>

                    {/* Mensagem de sucesso */}
                    <div className="space-y-2 mb-4 text-center">
                        <h2 className="text-lg font-semibold">Seu pedido está reservado!</h2>
                        <p className="text-sm">
                            Finalize o pagamento via Boleto para confirmar sua compra e ter acesso ao produto.
                        </p>
                    </div>
                    <div className="w-full p-4 rounded-lg bg-yellow-100 text-yellow-800 text-center">
                        <p className="text-sm font-medium">O acesso ao produto será enviado para o seu e-mail assim que o boleto for pago. A confirmação do pagamento pode levar até 48 horas.</p>
                    </div>
                    <div className="flex flex-col items-center mt-6 gap-6">
                        <div className="space-y-6 w-full">
                            <div className="p-4 border-2 border-dashed border-green-500 text-center break-words">
                                <p className="text-xs font-mono">{orderData.data.boleto?.linhaDigitavel || 'Linha digitável não disponível'}</p>
                            </div>
                            
                            {/* Código de barras */}
                            {orderData.data.boleto?.barcode && (
                                <div className="w-full flex justify-center bg-white p-4 rounded-lg">
                                    <img 
                                        src={orderData.data.boleto.barcode} 
                                        alt="Código de barras do boleto"
                                        className="w-full max-w-2xl h-auto"
                                        onError={(e) => {
                                            console.error("Erro ao carregar código de barras");
                                            (e.target as HTMLImageElement).style.display = 'none';
                                        }}
                                    />
                                </div>
                            )}

                            <Button
                                size="lg"
                                className={`w-full ${copied ? 'bg-green-600' : 'bg-green-700'} text-white shadow-theme-xs hover:bg-green-800 disabled:bg-gray-400`}
                                onClick={copyBoletoCode}
                                disabled={!orderData.data.boleto?.linhaDigitavel}
                            >
                                {copied ? "Código copiado!" : "Clique aqui para copiar a linha digitável"}
                            </Button>
                            <Button
                                size="lg"
                                className={`w-full bg-primary-700 text-white shadow-theme-xs hover:bg-primary-800 disabled:bg-gray-400`}
                                onClick={() => window.open(orderData.data.boleto?.pdf || orderData.data.boleto?.url, '_blank')}
                                disabled={!orderData.data.boleto?.pdf && !orderData.data.boleto?.url}
                            >
                                Visualizar boleto
                            </Button>
                        </div>

                        {/* Selo BaaS Asaas */}
                        <AsaasFinancialNotice variant="positivo" />
                    </div>
                </div>

                <div className="w-full md:w-3/5 mx-auto bg-white shadow-lg rounded-lg p-6 mt-6">
                    <div className="flex flex-col md:flex-row gap-8">
                        {/* Informações do pedido */}
                        <div className="flex-1">
                            <div className="space-y-4">
                                <h3 className="text-lg font-semibold">Informações do pedido</h3>
                                <div className="space-y-4">
                                    <p className="text-xs text-gray-600">
                                        <span className="font-medium">Data do pedido:</span> {new Date(orderData.data.order.createdAt).toLocaleString('pt-BR')}
                                    </p>
                                    <p className="text-xs text-gray-600">
                                        <span className="font-medium">Número do pedido:</span> {orderData.data.order.id.substring(0, 8).toUpperCase()}
                                    </p>
                                    <p className="text-xs text-gray-600">
                                        <span className="font-medium">Código do pedido:</span> {orderData.data.order.code.toUpperCase()}
                                    </p>
                                    {orderData.data.payments && orderData.data.payments[0] && (
                                        <p className="text-xs text-gray-600">
                                            <span className="font-medium">Código da transação:</span> {orderData.data.payments[0].id.substring(0, 8)}
                                        </p>
                                    )}
                                    <p className="text-xs text-gray-600">
                                        <span className="font-medium">Forma de Pagamento:</span> Boleto
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Resumo do Pedido */}
                        <div className="flex-1">
                            <div className="space-y-6">
                                <h3 className="text-lg font-semibold">Resumo do Pedido</h3>
                                <div>
                                    {orderData.data.items.map((item, index) => (
                                        <div key={item.id || index} className="flex items-center my-4">
                                            {/* Placeholder para imagem do produto */}
                                            <div className="w-12 h-12 bg-gray-200 rounded-md flex items-center justify-center mr-4">
                                                <img src={item.product?.imageUrl} alt={item.product?.name} className="w-full h-full object-cover" />
                                            </div>
                                            <div className="flex-1">
                                                {/* Aqui poderia mostrar o nome do produto, se disponível */}
                                                <p className="text-sm">{item.product?.name}</p>
                                            </div>
                                            <p className="text-sm">R$ {Number(item.unitPrice).toFixed(2)}</p>
                                        </div>
                                    ))}

                                    <hr className="my-4" />

                                    <div className="space-y-2">
                                        <div className="flex justify-between items-center">
                                            <p className="text-base font-semibold">Total</p>
                                            <p className="text-base font-semibold">
                                                R$ {Number(orderData.data.order.totalAmount).toFixed(2)}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="w-full md:w-3/5 mx-auto bg-white shadow-lg rounded-lg p-6 mt-6 justify-between">
                    <div className="flex flex-col md:flex-row gap-8">
                        {/* Ajuda */}
                        <div className="flex-1">
                            <div className="space-y-2">
                                <h3 className="text-sm">Precisa de ajuda?</h3>
                                <p className="text-xs text-gray-600">Entre em contato com o produtor.</p>
                            </div>
                        </div>

                        {/* Contato do Produtor */}
                        <div className="flex-1">
                            <div className="space-y-2">
                                <p className="text-xs text-gray-600">
                                    <span className="font-medium">Nome:</span> {orderData.data.company?.fantasyName}
                                </p>
                                <p className="text-xs text-gray-600">
                                    <span className="font-medium">E-mail:</span> {orderData.data.items[0]?.product?.supportInfo?.email}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default FinalizationBoleto;